/*
 * CreditNoteForm.tsx : Un formulaire pour ajouter ou modifier un avoir.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faChartLine,
	faClipboardList,
	faInfoCircle,
	faLink,
	faMoneyBillTransfer,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	finishFormSave,
	FooterTotal,
	FormSaveActions,
	InputField,
	PdfCard,
	RelatedItemsManager,
	SelectField,
	TextAreaField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import CreditNote
import { CreditNoteData } from '../types/types';
import { creditNotesApi } from '../services/creditNotesApi';
import { customerInvoicesApi } from 'features/customerInvoices/services/customerInvoicesApi';

// Import Activity
import { ActivityData } from 'features/activities/types/types';
import { activitiesApi } from 'features/activities/services/activitiesApi';

// Import LogisticElement
import { LogisticElementData } from 'features/logisticElements/types/types';
import { logisticElementsApi } from 'features/logisticElements/services/logisticElementsApi';

// Composant CreditNoteForm
const CreditNoteForm: React.FC = () => {
	// Hooks de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hooks pour l'utilisateur courant
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	// Hooks pour le formulaire courant
	const [formData, setFormData] = React.useState<CreditNoteData>({
		id: 0,
		referenceNumber: '',
		reason: '',
		customerInvoice: null,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hooks du select customerInvoice
	const [customerInvoiceOptions, setCustomerInvoiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCustomerInvoices, setLoadingCustomerInvoices] =
		React.useState(true);

	// Hook pour le prochain numéro de référence
	const [nextRef, setNextRef] = React.useState<string | null>(null);

	// Token d'authentification
	const token = localStorage.getItem('token') || '';

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Vérifier la présence d'un id (édition) ou non (création)
		if (id) {
			creditNotesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.customerInvoice) {
						setCustomerInvoiceOptions([
							{
								key: data.customerInvoice.id.toString(),
								label: data.customerInvoice.referenceNumber,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
		}
	}, [id]);

	// Lazy loading : options du select Factures clients
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCustomerInvoices) {
			customerInvoicesApi
				.listSelect(token)
				.then((items) => {
					setCustomerInvoiceOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCustomerInvoices(false));
		}
	}, [loading, loadingCustomerInvoices]);

	// Lazy load du prochain numéro de référence (création uniquement)
	React.useEffect(() => {
		// Vérification de la création d'un nouvel avoir
		if (id) return;

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			creditNotesApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					// Mise à jour du numéro de référence dans le formulaire
					setFormData((prevData) => ({
						...prevData,
						referenceNumber: 'AV' + data.nextReference,
					}));
					setBaselineReady(true);
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/creditNotes');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			creditNotesApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			creditNotesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/creditNotes/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs et textareas
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'customerInvoice') {
			const selectedOption = customerInvoiceOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					customerInvoice: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Vérification des prérequis pour la génération du PDF
	const canGeneratePdf = () => {
		if (!formData.customerInvoice) {
			alert('Veuillez lier une facture client avant de générer le PDF.');
			return false;
		}
		return true;
	};

	// Calcul du total HT des activités liées (pour le pied de tableau)
	const activityTotal = (formData.activities ?? []).reduce(
		(sum, act) => sum + Number(act.unitPriceExclTax ?? 0),
		0
	);

	// Calcul du total HT des éléments logistiques liés (pour le pied de tableau)
	const logisticTotal = (formData.logisticElements ?? []).reduce(
		(sum, el) =>
			sum + Number(el.unitPriceExclTax ?? 0) * Number(el.quantity ?? 1),
		0
	);

	// Render : Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faMoneyBillTransfer}
				title={id ? "Édition d'un avoir" : 'Nouvel avoir'}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES : informations générales + PDF */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE : informations générales */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<InputField
							label="Numéro de référence"
							name="referenceNumber"
							value={formData.referenceNumber}
							onChange={handleChange}
							loading={loading}
							placeholder="Chargement..."
							readOnly={id ? false : true}
						/>
						<TextAreaField
							label="Raison"
							name="reason"
							value={formData.reason || ''}
							onChange={handleChange}
							loading={loading}
							placeholder="Ex : Erreur de facturation"
						/>
					</CardSection>

					{/* COLONNE DROITE : facture liée + PDF */}
					<div className="flex flex-col gap-6">
						<CardSection icon={faLink} title="Autres liens">
							<SelectField
								label="Facture client"
								selectedKey={formData.customerInvoice?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'customerInvoice',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={customerInvoiceOptions}
								loading={loading}
								optionsLoading={loadingCustomerInvoices}
								entityHref={
									formData.customerInvoice?.id
										? `/app/customerInvoices/${formData.customerInvoice.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										customerInvoice: null,
									}))
								}
							/>
						</CardSection>

						<PdfCard
							resource="credit-notes"
							entity={formData}
							onEntityUpdate={(updated) => setFormData(updated)}
							checkCanGenerate={canGeneratePdf}
							loading={loading}
							size="md"
							className="w-full"
						/>
					</div>
				</div>

				{/* 1 COLONNE PLEINE LARGEUR : activités */}
				<div className="grid grid-cols-1 gap-6">
					{/* TABLEAU DES ACTIVITÉS À RECRÉDITER */}
					<CardSection icon={faChartLine} title="Activités à recréditer">
						<RelatedItemsManager<ActivityData>
							linkedItems={formData.activities || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, activities: items }))
							}
							fetchAll={() => {
								const token = localStorage.getItem('token');
								if (!token) return Promise.resolve([]);
								return activitiesApi
									.listPaginated(token, { page: 1, limit: 5000 })
									.then((res) => ('data' in res ? res.data : res));
							}}
							isLinked={(a) => !!a.creditNote?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'beginDate', label: 'Date' },
								{ key: 'origin', label: 'Origine' },
								{ key: 'service', label: 'Service' },
								{
									key: 'durationHours',
									label: 'Durée',
									render: (_value: unknown, item: ActivityData) => {
										if (item.durationHours == null) return '—';
										const totalMinutes = Math.round(item.durationHours * 60);
										const hours = Math.floor(totalMinutes / 60);
										const minutes = totalMinutes % 60;
										return minutes > 0
											? `${hours}h${String(minutes).padStart(2, '0')}`
											: `${hours}h`;
									},
								},
								{
									key: 'unitPriceExclTax',
									label: 'Tarif HT',
									render: (_value: unknown, item: ActivityData) =>
										item.unitPriceExclTax != null
											? new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(Number(item.unitPriceExclTax))
											: '—',
								},
								{
									key: 'project',
									label: 'Affaire',
									render: (_value: unknown, item: ActivityData) => {
										if (!item.project?.id) return '—';
										return (
											<a
												href={`/app/projects/${item.project.id}/edit`}
												className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
												onClick={(e) => e.stopPropagation()}
											>
												{item.project.referenceNumber ?? `#${item.project.id}`}
											</a>
										);
									},
								},
							]}
							linkedTitle="Liées"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/activities"
							linkedFooterTotals={
								(formData.activities ?? []).length > 0
									? ([
											{
												columnKey: 'unitPriceExclTax' as const,
												value: new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(activityTotal),
											},
										] satisfies FooterTotal<ActivityData>[])
									: []
							}
						/>
					</CardSection>

					{/* TABLEAU DES ÉLÉMENTS LOGISTIQUES À RECRÉDITER */}
					<CardSection
						icon={faClipboardList}
						title="Éléments logistiques à recréditer"
					>
						<RelatedItemsManager<LogisticElementData>
							linkedItems={formData.logisticElements || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, logisticElements: items }))
							}
							fetchAll={() =>
								logisticElementsApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => ('data' in res ? res.data : res))
							}
							isLinked={(le) => !!le.creditNote?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'date', label: 'Date' },
								{ key: 'label', label: 'Libellé' },
								{ key: 'product', label: 'Produit' },
								{ key: 'service', label: 'Service' },
								{
									key: 'unitPriceExclTax',
									label: 'Tarif HT',
									render: (_value: unknown, item: LogisticElementData) =>
										item.unitPriceExclTax != null
											? new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(Number(item.unitPriceExclTax))
											: '—',
								},
								{ key: 'quantity', label: 'Quantité' },
								{
									key: 'project',
									label: 'Affaire',
									render: (_value: unknown, item: LogisticElementData) => {
										if (!item.project?.id) return '—';
										return (
											<a
												href={`/app/projects/${item.project.id}/edit`}
												className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
												onClick={(e) => e.stopPropagation()}
											>
												{item.project.referenceNumber ?? `#${item.project.id}`}
											</a>
										);
									},
								},
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/logisticElements"
							linkedFooterTotals={
								(formData.logisticElements ?? []).length > 0
									? ([
											{
												columnKey: 'unitPriceExclTax' as const,
												value: new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(logisticTotal),
											},
										] satisfies FooterTotal<LogisticElementData>[])
									: []
							}
						/>
					</CardSection>
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/creditNotes')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default CreditNoteForm;
